import React from "react";
import { motion } from "framer-motion";
import { Hexagon } from "lucide-react";

const DEFAULT_BG = "https://images.unsplash.com/photo-1580748141549-71748dbe0bdc?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA2ODl8MHwxfHNlYXJjaHwyfHxpY2UlMjBob2NrZXklMjBhY3Rpb258ZW58MHx8fHwxNzgxMjk2MzYxfDA&ixlib=rb-4.1.0&q=85";

/**
 * NHL-broadcast-grade page banner. Used by all secondary pages
 * (About, Teams, Schedule, Standings, Players, News, Sponsorships,
 * Partnerships, Academies, Media, Contact).
 *
 * Photo dominates the right side; copy is left-anchored over a deep-navy
 * gradient mask. Continuous Ken-Burns scale on the photo, staggered
 * entrance for overline → title → subtitle.
 */
export default function PageHero({ overline, title, subtitle, bgImage }) {
  const photo = bgImage || DEFAULT_BG;
  return (
    <section className="relative pt-20 md:pt-24 pb-10 md:pb-14 overflow-hidden min-h-[44vh] md:min-h-[52vh] flex items-center bg-[#020A1A]">
      {/* Ken-Burns background photo */}
      <motion.div
        animate={{ scale: [1, 1.06, 1] }}
        transition={{ duration: 26, repeat: Infinity, ease: "linear" }}
        className="absolute inset-0 z-0"
      >
        <div
          className="absolute inset-0 bg-cover bg-center md:bg-[78%_center]"
          style={{ backgroundImage: `url(${photo})` }}
          aria-hidden
        />
      </motion.div>

      {/* Cinematic gradient masks — copy stays legible, photo carries the right */}
      <div className="absolute inset-0 z-10 bg-gradient-to-r from-[#020A1A] via-[#020A1A]/90 md:via-[#020A1A]/60 to-transparent" />
      <div className="absolute inset-0 z-10 bg-gradient-to-t from-[#020A1A] via-transparent to-[#020A1A]/40" />
      <div className="absolute inset-0 z-10 speed-lines opacity-25 mix-blend-overlay pointer-events-none" />

      {/* Color glows */}
      <div className="absolute -bottom-16 -left-16 h-[360px] w-[360px] rounded-full blur-[100px] bg-pshl-electric/22 z-10 pointer-events-none" />
      <div className="absolute -top-10 right-0 h-[300px] w-[300px] rounded-full blur-[100px] bg-pshl-red/15 z-10 pointer-events-none" />

      <div className="relative z-20 pshl-container w-full">
        <motion.div
          initial={{ opacity: 0, x: -24 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ duration: 0.7, ease: [0.16, 1, 0.3, 1] }}
          className="max-w-3xl"
        >
          {overline && (
            <motion.div
              initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.1, duration: 0.45 }}
              className="inline-flex items-center gap-2 border border-pshl-electric/50 bg-pshl-electric/12 px-3.5 py-1.5 backdrop-blur-md mb-5"
            >
              <motion.div animate={{ opacity: [1, 0.4, 1] }} transition={{ duration: 2.2, repeat: Infinity }}>
                <Hexagon className="h-3.5 w-3.5 text-pshl-electric" />
              </motion.div>
              <span className="text-[10px] sm:text-xs uppercase tracking-[0.3em] text-white font-display font-semibold">
                {overline}
              </span>
            </motion.div>
          )}

          <motion.h1
            initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.2, duration: 0.55 }}
            className="font-display text-5xl sm:text-6xl lg:text-7xl xl:text-8xl text-white tracking-tighter leading-[0.92] uppercase drop-shadow-[0_4px_24px_rgba(0,0,0,0.55)]"
          >
            {title}
          </motion.h1>

          {subtitle && (
            <motion.p
              initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.3, duration: 0.5 }}
              className="mt-6 max-w-2xl text-base md:text-lg text-white/85 leading-relaxed font-medium drop-shadow-md"
            >
              {subtitle}
            </motion.p>
          )}
        </motion.div>
      </div>
    </section>
  );
}
